import React, { useState, useEffect } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  Save, 
  AlertCircle, 
  Settings as SettingsIcon,
  Mail,
  Globe,
  Lock,
  DollarSign,
  BookOpen,
  MessageCircle,
  RefreshCw,
  CreditCard,
  Palette,
  Share2,
  Link as LinkIcon,
  ShieldCheck,
  Zap,
  Info,
  Smartphone,
  ExternalLink,
  CheckCircle2,
  MonitorPlay,
  BellRing
} from 'lucide-react';
import { useToast } from '../../components/common/Toast';

const SystemSettings = () => {
  const { settings, loading, error, updateSettings, refreshSettings } = useSettings();
  const toast = useToast();
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // مزامنة البيانات مع النموذج (المنطق الأصلي)
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      setFormData(settings);
    }
  }, [settings]);

  const handleInputChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // منطق حفظ الإعدادات (كامل بدون اختصار مع تنظيف البيانات)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const cleanedData = {};
      for (const [key, value] of Object.entries(formData)) {
        if (value === undefined || value === null || value === '') continue;
        if (Array.isArray(value) && key === 'allowedFileTypes') {
          cleanedData[key] = value;
          continue;
        }
        cleanedData[key] = value;
      }
      
      await updateSettings(cleanedData);
      toast.success('تم تحديث إعدادات النظام بنجاح');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'حدث خطأ أثناء محاولة الحفظ');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshSettings();
      toast.success('تمت مزامنة البيانات من الخادم');
    } catch (err) {
      toast.error('فشل في تحديث البيانات');
    }
  };

  const tabs = [
    { id: 'general', name: 'المنصة والبريد', icon: Globe, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'security', name: 'الأمان والوصول', icon: Lock, color: 'text-rose-600', bg: 'bg-rose-50' },
    { id: 'finance', name: 'العمولات والدفع', icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { id: 'courses', name: 'سياسة المحتوى', icon: BookOpen, color: 'text-purple-600', bg: 'bg-purple-50' },
    { id: 'whatsapp', name: 'واتساب وتواصل', icon: MessageCircle, color: 'text-green-600', bg: 'bg-green-50' },
    { id: 'identity', name: 'الهوية البصرية', icon: Palette, color: 'text-amber-600', bg: 'bg-amber-50' },
    { id: 'social', name: 'شبكات التواصل', icon: Share2, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  ];

  if (loading && Object.keys(formData).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحميل إعدادات النظام...</p>
      </div>
    );
  }

  if (error && Object.keys(formData).length === 0) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-red-100">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">تعذر الوصول للإعدادات</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">{error}</p>
          <button onClick={handleRefresh} className="bg-primary-600 text-white px-10 py-3 rounded-2xl font-bold hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto">
            <RefreshCw size={18} /> إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Executive Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <SettingsIcon className="text-primary-600 bg-primary-50 p-1.5 rounded-xl" size={36} />
            إدارة إعدادات النظام
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2 mr-12">
            <ShieldCheck className="text-emerald-500" size={18} />
            التحكم في خيارات المنصة، الأمان، التدفقات المالية، وتخصيص تجربة المستخدم
          </p>
        </div>

        <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              className="bg-white border border-gray-100 text-gray-700 p-3 rounded-2xl shadow-sm hover:bg-primary-50 hover:text-primary-600 transition-all"
              title="تحديث البيانات"
            >
              <RefreshCw size={22} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
                type="submit"
                form="system-settings-form"
                disabled={isSaving}
                className="bg-primary-600 text-white px-8 py-3.5 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center gap-2 transform active:scale-95 disabled:opacity-50"
            >
                {isSaving ? <RefreshCw className="animate-spin" size={20} /> : <Save size={20} />}
                حفظ كافة التغييرات
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* Left Column: Navigation Tabs Sidebar */}
        <div className="lg:col-span-3 space-y-4">
            <div className="bg-white rounded-[2.2rem] p-4 shadow-sm border border-gray-100 space-y-2 sticky top-24">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] px-4 py-2">أقسام الإعدادات</p>
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                            w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-black text-sm transition-all duration-300
                            ${activeTab === tab.id
                                ? `${tab.bg} ${tab.color} shadow-lg shadow-gray-100 translate-x-[-4px]`
                                : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                            }
                        `}
                    >
                        <tab.icon size={20} />
                        {tab.name}
                    </button>
                ))}
            </div>

            {/* Quick Status Info */}
            <div className="bg-gray-900 rounded-[2.2rem] p-8 text-white relative overflow-hidden group shadow-2xl">
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>
                <div className="relative z-10 space-y-4">
                    <div className="bg-primary-600 w-10 h-10 rounded-xl flex items-center justify-center">
                        <Zap size={20} />
                    </div>
                    <h4 className="font-black text-sm">حالة المزامنة</h4>
                    <p className="text-gray-400 text-[10px] leading-relaxed font-medium uppercase tracking-widest">
                        إصدار النظام: v2.4.0 <br />
                        الحالة: متزامن ✓
                    </p>
                </div>
            </div>
        </div>

        {/* Right Column: Dynamic Tab Content */}
        <div className="lg:col-span-9">
            <form id="system-settings-form" onSubmit={handleSubmit} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden min-h-[700px] animate-in fade-in duration-500">
                <div className="p-8 lg:p-12">
                    
                    {/* SECTION: GENERAL & EMAIL */}
                    {activeTab === 'general' && (
                        <div className="space-y-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-8">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-black text-gray-900">المنصة والبريد الإلكتروني</h2>
                                    <p className="text-gray-500 text-sm">تخصيص البيانات التعريفية للمنصة وإعدادات المراسلة</p>
                                </div>
                                <div className="p-3 bg-blue-50 rounded-2xl text-blue-600"><Globe size={28} /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">اسم المنصة الرسمي</label>
                                    <input
                                        type="text"
                                        value={formData.platform_name || ''}
                                        onChange={(e) => handleInputChange('platform_name', e.target.value)}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-900"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رابط الموقع (URL)</label>
                                    <div className="relative">
                                        <input
                                            type="url"
                                            value={formData.platform_url || ''}
                                            onChange={(e) => handleInputChange('platform_url', e.target.value)}
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-900 ltr-input"
                                        />
                                        <LinkIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                    </div>
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">وصف المنصة العام (SEO)</label>
                                    <textarea
                                        value={formData.platform_description || ''}
                                        onChange={(e) => handleInputChange('platform_description', e.target.value)}
                                        rows="3"
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-medium text-gray-700 resize-none"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">بريد إرسال الفواتير</label>
                                    <div className="relative">
                                        <input
                                            type="email"
                                            value={formData.billing_email || ''}
                                            onChange={(e) => handleInputChange('billing_email', e.target.value)}
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-900 ltr-input"
                                        />
                                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">اسم مرسل رسائل البريد</label>
                                    <input
                                        type="text"
                                        value={formData.email_sender_name || ''}
                                        onChange={(e) => handleInputChange('email_sender_name', e.target.value)}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-900"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION: SECURITY */}
                    {activeTab === 'security' && (
                        <div className="space-y-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-8">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-black text-gray-900 text-rose-600">الأمان والوصول</h2>
                                    <p className="text-gray-500 text-sm">حماية المنصة من محاولات الدخول غير المصرح بها</p>
                                </div>
                                <div className="p-3 bg-rose-50 rounded-2xl text-rose-600"><Lock size={28} /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">أقصى محاولات تسجيل فاشلة</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.max_login_attempts || 5}
                                        onChange={(e) => handleInputChange('max_login_attempts', parseInt(e.target.value))}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-rose-500/10 focus:bg-white transition-all font-black text-gray-900"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">مدة قفل الحساب (دقائق)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.account_lock_duration || 30}
                                        onChange={(e) => handleInputChange('account_lock_duration', parseInt(e.target.value))}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-rose-500/10 focus:bg-white transition-all font-black text-gray-900"
                                    />
                                </div>
                            </div>

                            <div className="bg-rose-50/30 p-8 rounded-[2rem] border border-rose-100 flex items-center justify-between group hover:bg-rose-50 transition-all">
                                <div className="flex items-center gap-5">
                                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-rose-600 shadow-sm border border-rose-100 group-hover:scale-110 transition-transform">
                                        <BellRing size={28} />
                                    </div>
                                    <div>
                                        <p className="text-base font-black text-gray-900 uppercase">التحقق من البريد الإلكتروني</p>
                                        <p className="text-xs font-bold text-rose-600/60 uppercase tracking-tighter">يجب على جميع المستخدمين الجدد توثيق بريدهم قبل الاستخدام</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.require_email_verification === true || formData.require_email_verification === 'true'}
                                        onChange={(e) => handleInputChange('require_email_verification', e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.5rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:right-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-rose-600"></div>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* SECTION: FINANCE */}
                    {activeTab === 'finance' && (
                        <div className="space-y-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-8">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-black text-gray-900 text-emerald-600">العمولات والسياسة المالية</h2>
                                    <p className="text-gray-500 text-sm">تحديد النسب المئوية وعملات النظام الافتراضية</p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600"><DollarSign size={28} /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">نسبة عمولة المنصة (%)</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            value={formData.commission_percentage || 0}
                                            onChange={(e) => handleInputChange('commission_percentage', parseFloat(e.target.value))}
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:bg-white transition-all font-black text-gray-900"
                                        />
                                        <DollarSign size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                    </div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase mr-4">يتم خصم هذه النسبة تلقائياً من أرباح المدرسين</p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">العملة الأساسية للنظام</label>
                                    <select
                                        value={formData.default_currency || 'EGP'}
                                        onChange={(e) => handleInputChange('default_currency', e.target.value)}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                                    >
                                        <option value="EGP">الجنيه المصري (EGP)</option>
                                        <option value="SAR">الريال السعودي (SAR)</option>
                                        <option value="USD">الدولار الأمريكي (USD)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="bg-emerald-50/50 p-6 rounded-[2rem] border border-emerald-100 flex items-start gap-4">
                                <Info size={24} className="text-emerald-600 mt-1" />
                                <div>
                                    <h4 className="font-black text-emerald-900 text-sm uppercase">تأثير تغيير العملة</h4>
                                    <p className="text-xs font-medium text-emerald-700 mt-1 leading-relaxed">تغيير العملة الأساسية لن يغير القيم العددية المسجلة مسبقاً، بل سيغير فقط الرمز المعروض للطالب في واجهة الكورسات والاشتراكات.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION: COURSES */}
                    {activeTab === 'courses' && (
                        <div className="space-y-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-8">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-black text-gray-900 text-purple-600">إعدادات الكورسات والمحتوى</h2>
                                    <p className="text-gray-500 text-sm">تحديد القيود التقنية لرفع المحتوى التعليمي</p>
                                </div>
                                <div className="p-3 bg-purple-50 rounded-2xl text-purple-600"><BookOpen size={28} /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">أقصى عدد كورسات / مدرس</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.max_courses_per_teacher || 50}
                                        onChange={(e) => handleInputChange('max_courses_per_teacher', parseInt(e.target.value))}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-purple-500/10 focus:bg-white transition-all font-black text-gray-900"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">أقصى حجم للفيديو (ميجابايت)</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            min="1"
                                            value={formData.max_video_size_mb || 500}
                                            onChange={(e) => handleInputChange('max_video_size_mb', parseInt(e.target.value))}
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-purple-500/10 focus:bg-white transition-all font-black text-gray-900"
                                        />
                                        <MonitorPlay size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION: WHATSAPP */}
                    {activeTab === 'whatsapp' && (
                        <div className="space-y-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-8">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-black text-gray-900 text-green-600">قنوات التواصل والواتساب</h2>
                                    <p className="text-gray-500 text-sm">تحديد أرقام الدعم الفني وواتساب المنصة الرسمي</p>
                                </div>
                                <div className="p-3 bg-green-50 rounded-2xl text-green-600"><MessageCircle size={28} /></div>
                            </div>

                            <div className="bg-primary-600 p-6 rounded-[1.8rem] shadow-xl shadow-primary-100 flex items-start gap-4">
                                <ShieldCheck className="text-white mt-1" />
                                <p className="text-sm font-bold text-white leading-relaxed">
                                    المدير فقط هو المسؤول المركزي عن الدعم والاشتراكات. الأرقام المدخلة هنا هي التي ستظهر للطلاب في كافة واجهات الدفع والاستفسار.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رقم واتساب المنصة الأساسي</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="201xxxxxxxxx"
                                            value={formData.platform_whatsapp || ''}
                                            onChange={(e) => handleInputChange('platform_whatsapp', e.target.value)}
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:bg-white transition-all font-black text-gray-900 ltr-input"
                                        />
                                        <Smartphone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رقم الهاتف العام للدعم</label>
                                    <input
                                        type="text"
                                        placeholder="+201xxxxxxxxx"
                                        value={formData.general_contact_phone || ''}
                                        onChange={(e) => handleInputChange('general_contact_phone', e.target.value)}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:bg-white transition-all font-bold text-gray-900 ltr-input"
                                    />
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">عنوان المقر الإداري</label>
                                    <input
                                        type="text"
                                        placeholder="مثال: القاهرة، مدينة نصر..."
                                        value={formData.office_address || ''}
                                        onChange={(e) => handleInputChange('office_address', e.target.value)}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-green-500/10 focus:bg-white transition-all font-bold text-gray-900"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION: PAYMENT METHODS */}
                    {activeTab === 'finance' && (
                        <div className="space-y-10 mt-12 pt-12 border-t border-gray-100">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="space-y-1">
                                    <h2 className="text-xl font-black text-gray-900">أرقام حسابات الدفع السريع</h2>
                                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">تظهر للطلاب في صفحة تأكيد الدفع</p>
                                </div>
                                <CreditCard className="text-amber-500" size={32} />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="p-6 bg-red-50 rounded-[2rem] border border-red-100 space-y-4">
                                    <label className="text-[10px] font-black text-red-600 uppercase tracking-widest block text-center">فودافون كاش 🔴</label>
                                    <input
                                        type="text"
                                        value={formData.vodafone_cash_number || ''}
                                        onChange={(e) => handleInputChange('vodafone_cash_number', e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-red-200 rounded-xl font-black text-center text-gray-900"
                                        placeholder="010xxxxxxx"
                                    />
                                </div>
                                <div className="p-6 bg-purple-50 rounded-[2rem] border border-purple-100 space-y-4">
                                    <label className="text-[10px] font-black text-purple-600 uppercase tracking-widest block text-center">إنستاباي 🟣</label>
                                    <input
                                        type="text"
                                        value={formData.instapay_id || ''}
                                        onChange={(e) => handleInputChange('instapay_id', e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-purple-200 rounded-xl font-black text-center text-gray-900"
                                        placeholder="name@instapay"
                                    />
                                </div>
                                <div className="p-6 bg-orange-50 rounded-[2rem] border border-orange-100 space-y-4">
                                    <label className="text-[10px] font-black text-orange-600 uppercase tracking-widest block text-center">أورانج كاش 🟠</label>
                                    <input
                                        type="text"
                                        value={formData.orange_cash_number || ''}
                                        onChange={(e) => handleInputChange('orange_cash_number', e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl font-black text-center text-gray-900"
                                        placeholder="011xxxxxxx"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION: IDENTITY */}
                    {activeTab === 'identity' && (
                        <div className="space-y-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-8">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-black text-gray-900 text-amber-600">الهوية البصرية والألوان</h2>
                                    <p className="text-gray-500 text-sm">تخصيص اللوجو والألوان الأساسية لواجهات المنصة</p>
                                </div>
                                <div className="p-3 bg-amber-50 rounded-2xl text-amber-600"><Palette size={28} /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رابط اللوجو (SVG/PNG)</label>
                                    <input
                                        type="text"
                                        value={formData.site_logo_url || ''}
                                        onChange={(e) => handleInputChange('site_logo_url', e.target.value)}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-amber-500/10 focus:bg-white transition-all font-bold text-gray-900 ltr-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رابط الأيقونة (Favicon)</label>
                                    <input
                                        type="text"
                                        value={formData.site_favicon_url || ''}
                                        onChange={(e) => handleInputChange('site_favicon_url', e.target.value)}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-amber-500/10 focus:bg-white transition-all font-bold text-gray-900 ltr-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">اللون الأساسي (Primary)</label>
                                    <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-2xl border border-gray-100 group hover:bg-white transition-all">
                                        <input
                                            type="color"
                                            value={formData.brand_primary_color || '#0066cc'}
                                            onChange={(e) => handleInputChange('brand_primary_color', e.target.value)}
                                            className="w-14 h-14 rounded-xl border-none cursor-pointer p-0"
                                        />
                                        <input
                                            type="text"
                                            value={formData.brand_primary_color || ''}
                                            onChange={(e) => handleInputChange('brand_primary_color', e.target.value)}
                                            className="flex-1 bg-transparent border-none font-black text-gray-900 uppercase focus:ring-0"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">اللون الثانوي (Secondary)</label>
                                    <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-2xl border border-gray-100 group hover:bg-white transition-all">
                                        <input
                                            type="color"
                                            value={formData.brand_secondary_color || '#f3f4f6'}
                                            onChange={(e) => handleInputChange('brand_secondary_color', e.target.value)}
                                            className="w-14 h-14 rounded-xl border-none cursor-pointer p-0"
                                        />
                                        <input
                                            type="text"
                                            value={formData.brand_secondary_color || ''}
                                            onChange={(e) => handleInputChange('brand_secondary_color', e.target.value)}
                                            className="flex-1 bg-transparent border-none font-black text-gray-900 uppercase focus:ring-0"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION: SOCIAL LINKS */}
                    {activeTab === 'social' && (
                        <div className="space-y-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-50 pb-8">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-black text-gray-900 text-indigo-600">شبكات التواصل والروابط</h2>
                                    <p className="text-gray-500 text-sm">إدارة حضور المنصة في المواقع الاجتماعية والروابط القانونية</p>
                                </div>
                                <div className="p-3 bg-indigo-50 rounded-2xl text-indigo-600"><Share2 size={28} /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {[
                                    { key: 'social_facebook_url', label: 'رابط فيسبوك 📘' },
                                    { key: 'social_twitter_url', label: 'رابط تويتر (X) 𝕏' },
                                    { key: 'social_youtube_url', label: 'رابط يوتيوب 📺' },
                                    { key: 'social_instagram_url', label: 'رابط إنستجرام 📷' },
                                    { key: 'site_terms_url', label: 'رابط الشروط والأحكام ⚖️' },
                                    { key: 'site_privacy_url', label: 'رابط سياسة الخصوصية 🔒' }
                                ].map((link) => (
                                    <div key={link.key} className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">{link.label}</label>
                                        <div className="relative">
                                            <input
                                                type="url"
                                                value={formData[link.key] || ''}
                                                onChange={(e) => handleInputChange(link.key, e.target.value)}
                                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:bg-white transition-all font-bold text-gray-900 ltr-input"
                                                placeholder="https://..."
                                            />
                                            <ExternalLink size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Submit Sticky Area */}
                <div className="p-8 border-t border-gray-50 bg-gray-50/30 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-gray-400">
                        <CheckCircle2 size={20} className="text-primary-500" />
                        <p className="text-xs font-bold uppercase tracking-widest">تأكد من مراجعة كافة الحقول قبل الحفظ النهائي</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => setActiveTab('general')}
                            className="px-6 py-3 rounded-xl font-bold text-gray-400 hover:text-gray-600 transition-all text-sm"
                        >
                            العودة للبداية
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-200 hover:bg-primary-700 transition-all transform active:scale-95 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSaving ? <RefreshCw className="animate-spin" size={20} /> : <Save size={20} />}
                            حفظ كافة الإعدادات
                        </button>
                    </div>
                </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;